import { CFO_SURFACES } from '../cfoTelemetry'
import { buildCfoInstantCardFromContext, buildCfoInstantCardKey } from './instantCards'
import type { CfoAnalysisContext, CfoAnalysisVisualSpec, CfoInstantCard } from './types'

type ResolveInput = {
  surface: string
  pageId: string
  sourceComponent?: string | null
  metricId?: string | null
  metricLabel?: string | null
  period?: string | null
  comparison?: string | null
  asOfLabel?: string | null
  sourceTables?: string[] | null
  selection?: Record<string, any> | null
  visual?: CfoAnalysisVisualSpec | null
  grounding?: CfoAnalysisContext['grounding'] | null
  pageFacts?: Record<string, any> | null
  instantCards?: CfoInstantCard[] | null
}

const trimStr = (value: unknown): string => String(value ?? '').trim()

const titleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())

const noDefaultFinancialCardSurfaces = new Set<string>([
  CFO_SURFACES.advanced,
  'analysis_profile',
  'cfo_analysis_profile',
  'settings',
  'cfo_settings',
  'ai_tuning',
  'ai-tuning',
  'cfo_ai_tuning',
])

const cardKeyForInput = (input: ResolveInput): string => buildCfoInstantCardKey({
  workbookId: null,
  pageId: input.pageId,
  activeSection: input.surface,
  period: input.period ?? null,
  comparison: input.comparison ?? null,
  asOfLabel: input.asOfLabel ?? null,
  metricId: input.metricId ?? null,
  metricLabel: input.metricLabel ?? null,
  sourceComponent: input.sourceComponent ?? null,
  sourceTables: input.sourceTables ?? null,
  selection: input.selection ?? null,
  grounding: input.grounding ?? null,
  instantCard: null,
})

const findPrebuiltCard = (input: ResolveInput): CfoInstantCard | null => {
  const cards = input.instantCards || []
  if (!cards.length) return null
  const expectedKey = cardKeyForInput(input)
  const byKey = cards.find((card) => card.cardKey === expectedKey)
  if (byKey) return byKey
  const source = trimStr(input.sourceComponent)
  const metric = trimStr(input.metricId)
  return cards.find((card) => (
    card.surface === input.surface &&
    (!source || card.sourceLineage?.sourceComponent === source || card.cardKind === source) &&
    (!metric || card.sourceLineage?.metricId === metric)
  )) || null
}

const pageFact = (input: ResolveInput, key: string): unknown => {
  const facts = input.pageFacts || {}
  return facts[key] ?? facts[key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())]
}

const surfaceCardKind = (surface: string, sourceComponent?: string | null): string => {
  const source = trimStr(sourceComponent)
  if (source) return source
  switch (surface) {
    case CFO_SURFACES.home:
      return 'home_evidence'
    case CFO_SURFACES.financials:
      return 'statement_evidence'
    case CFO_SURFACES.cash:
      return 'cash_evidence'
    case CFO_SURFACES.ratios:
      return 'ratio_evidence'
    case CFO_SURFACES.ar:
      return 'ar_evidence'
    case CFO_SURFACES.forecast:
      return 'forecast_evidence'
    case CFO_SURFACES.scenarios:
      return 'scenario_evidence'
    case CFO_SURFACES.valuation:
      return 'valuation_evidence'
    case CFO_SURFACES.presentations:
      return 'presentation_block'
    case CFO_SURFACES.explore:
      return 'explore_selection'
    default:
      return 'evidence'
  }
}

const deterministicReadForSurface = (surface: string, base: CfoInstantCard): string => {
  if (trimStr(base.deterministicRead)) return base.deterministicRead
  const metric = base.primaryMetric.label || base.title
  switch (surface) {
    case CFO_SURFACES.home:
      return `${metric} is the selected Home brief driver.`
    case CFO_SURFACES.financials:
      return `${metric} is the selected statement line.`
    case CFO_SURFACES.ar:
      return `${metric} is the selected receivables evidence.`
    case CFO_SURFACES.forecast:
      return `${metric} is the selected forecast variance.`
    case CFO_SURFACES.scenarios:
      return `${metric} is the selected scenario driver.`
    case CFO_SURFACES.valuation:
      return `${metric} is the selected valuation driver.`
    case CFO_SURFACES.presentations:
      return `${metric} is the selected report block.`
    default:
      return `${metric} is the selected evidence for this analysis.`
  }
}

export function resolveCfoInstantCard(input: ResolveInput): CfoInstantCard | null {
  const prebuilt = findPrebuiltCard(input)
  if (prebuilt) return prebuilt

  const surface = trimStr(input.surface || input.pageId)
  const sourceComponent = trimStr(input.sourceComponent) || surfaceCardKind(surface)
  const hasEvidence =
    trimStr(input.metricId) ||
    trimStr(input.metricLabel) ||
    trimStr(sourceComponent) ||
    input.visual ||
    input.grounding ||
    input.selection
  if (!hasEvidence || noDefaultFinancialCardSurfaces.has(surface)) return null

  const metricLabel =
    trimStr(input.metricLabel) ||
    trimStr((input.selection as any)?.label) ||
    trimStr((input.selection as any)?.metric_label) ||
    titleCase(trimStr(input.metricId) || sourceComponent)
  const sourceFingerprint =
    trimStr((input.selection as any)?.source_fingerprint) ||
    trimStr((input.selection as any)?.signature) ||
    trimStr(pageFact(input, 'source_fingerprint')) ||
    trimStr(pageFact(input, 'artifact_fingerprint')) ||
    [
      trimStr(surface),
      trimStr(input.period),
      trimStr(input.comparison),
      trimStr(pageFact(input, 'contract_revision')),
    ].filter(Boolean).join(':')

  const context: CfoAnalysisContext = {
    workbookId: null,
    pageId: input.pageId,
    activeSection: surface,
    period: input.period ?? null,
    comparison: input.comparison ?? null,
    asOfLabel: input.asOfLabel ?? null,
    metricId: input.metricId ?? (input.grounding?.metric_id || null),
    metricLabel,
    sourceComponent,
    sourceTables: input.sourceTables ?? null,
    selection: {
      ...(input.selection || {}),
      source_fingerprint: sourceFingerprint || (input.selection as any)?.source_fingerprint || null,
    },
    grounding: input.grounding ?? null,
    instantCard: null,
  }
  const card = buildCfoInstantCardFromContext({ context, visual: input.visual ?? null })
  return {
    ...card,
    cardKind: surfaceCardKind(surface, sourceComponent),
    deterministicRead: deterministicReadForSurface(surface, card),
    prescriptiveSeed: [
      card.prescriptiveSeed,
      `This is a ${surface} first-layer card; use only the compact instant_card and explicit action target unless the user chooses a deeper drilldown.`,
    ].filter(Boolean).join(' '),
    freshness: {
      ...card.freshness,
      sourceFingerprint: sourceFingerprint || card.freshness.sourceFingerprint || null,
    },
  }
}
